import dayjs from "dayjs";
import _, { flatten, pickBy } from "lodash";
import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { BarChart } from "./BarChart";
import { CircleChart } from "./CircleChart";
import { LineChart } from "./LineChart";
import { ModalFilterConditionSection } from "./ModalFilterConditionSection";
import { OrgChartControls } from "./OrgChartControls";
import { TableChart } from "./TableChart";
import useLabels from "../../../containers/FacilityDetailsPage/hooks/useLabels";
import { useMeWithOrganizationImpersonation } from "../../../hooks/organizationImpersonation";
import { useCategories, useLocationMunis } from "../../../hooks/queries";
import {
  ChartData,
  AnalysisChartFilterData,
  OrgTimeSeriesMetricsData,
  OrgGroupAggregationMetricsData,
  OrgGroupedTimeSeriesMetricsData,
} from "../../../models/ChartCollectionData";
import {
  FacilityFilterConditionData,
  createDefaultConditionFormData,
} from "../../../models/FacilityFilterConditionData";
import { MunicipalityGroupData } from "../../../models/MunicipalityData";
import DataSvc from "../../../services/dataSvc";
import { getFromToDate } from "../../../utils/chartPeriodUtils";
import { DateRangeArea } from "../DateRangeArea";

import "./styles.scss";

export interface ICollectionChartAreaProps extends RouteComponentProps<any> {
  indivFacilityMode?: boolean;
  chartData: ChartData;
  totalLength: number;
  chartIndex: number;
  onClickCopyChart?: () => void;
  onClickEditChart: () => void;
  onClickMoveUp?: () => void;
  onClickMoveDown?: () => void;
  onUpdate?: (id: number, payload: any) => void;
}

const defaultConditionFormData = createDefaultConditionFormData();

export const CollectionChartArea: React.FunctionComponent<
  ICollectionChartAreaProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`analysisCollectionsPage.collectionChartArea.${key}`);

  const {
    chartData,
    totalLength,
    chartIndex,
    indivFacilityMode,
    onClickEditChart,
    onClickMoveUp,
    onClickMoveDown,
    onUpdate,
  } = props;

  // Only for line chart
  const [timeUnit, setTimeUnit] = useState<"day" | "week" | "month">("day");
  const [usePrevYear, setUsePrevYear] = useState<boolean>(false);

  const [shownModalFilterCondition, setShownModalFilterCondition] =
    useState<boolean>(false); // false

  const [conditionFormData, setConditionFormData] =
    useState<FacilityFilterConditionData>(defaultConditionFormData);

  const [chartDataShown, setChartDataShown] = useState<ChartData>({
    name: "",
    collection_id: 0,
    template_id: 0,
    template_name: "",
    period_start: "",
    period_end: "",
    period_option: "custom",
    type: "bar",
    compare_to_prev: false,
    time_unit: "day",
    metrics: "",
    aggregation: "sum",
    grouping1: "",
    grouping2: "",
    prefecture_filter: "",
    municipality_filter: "",
    category_filter: "",
    label_filter: "",
    display_order: 0,
    is_template: true,
    id: 0,
    created_at: "",
    created_by: 1,
    last_modified_at: "",
    last_modified_by: 1,
  });
  const [chartDetailsData, setChartDetailsData] =
    useState<OrgGroupAggregationMetricsData>({
      organization_id: 0,
      start_date: "",
      end_date: "",
      data: [],
    });

  const [chartDetailsForLinesData, setChartDetailsForLinesData] = useState<
    OrgTimeSeriesMetricsData[]
  >([]);
  const [
    prevYearChartDetailsForLinesData,
    setPrevYearChartDetailsForLinesData,
  ] = useState<OrgTimeSeriesMetricsData[]>([]);

  const me = useMeWithOrganizationImpersonation();
  const organizationId = me?.organization_id;

  const { data: labels } = useLabels(
    pickBy({
      organization_id: organizationId,
    })
  );
  const { data: categories } = useCategories();
  const { data: municipalities } = useLocationMunis(
    me?.id,
    organizationId || ""
  );

  useEffect(() => {
    if (labels && chartData.label_filter) {
      const ids = chartData.label_filter.split(",");
      setConditionFormData((prev) => ({
        ...prev,
        labels: labels.filter((l) => ids.indexOf(l.id.toString()) >= 0),
      }));
    }
  }, [labels, chartData]);

  useEffect(() => {
    if (categories && chartData.category_filter) {
      const ids = chartData.category_filter.split(",");
      setConditionFormData((prev) => ({
        ...prev,
        categories: categories.filter((c) => ids.indexOf(c.id.toString()) >= 0),
      }));
    }
  }, [categories, chartData]);

  useEffect(() => {
    if (municipalities && chartData.municipality_filter) {
      const ids = chartData.municipality_filter.split(",");
      const prefArray = municipalities.filter((item) => {
        return item.type === "pref";
      });

      const municipalitiesArray: MunicipalityGroupData[] = [];

      prefArray.forEach((itemPref) => {
        const muniArray = municipalities.filter((itemMuni) => {
          return (
            itemMuni.type === "muni" &&
            itemMuni.parent_external_id === itemPref.external_id
          );
        });

        municipalitiesArray.push({
          name: itemPref.name,
          muniList: muniArray.filter((m) => ids.indexOf(m.id.toString()) >= 0),
        });
      });

      setConditionFormData((prev) => ({
        ...prev,
        municipalities: municipalitiesArray,
      }));
    }
  }, [municipalities, chartData]);

  useEffect(() => {
    if (!!chartData) {
      setChartDataShown(_.cloneDeep(chartData));
      setTimeUnit(chartData.time_unit);
      setUsePrevYear(chartData.compare_to_prev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  const lastFetchParams = useRef<any>(null);

  useEffect(() => {
    if (chartDataShown.collection_id !== 0) {
      const lineMetricsArray = JSON.parse(chartDataShown.metrics);
      const groupArray: string[] = [];
      if (chartDataShown.grouping1) {
        groupArray.push(chartDataShown.grouping1);
      }
      if (
        chartDataShown.grouping2 &&
        ["table", "pie"].includes(chartDataShown.type)
      ) {
        groupArray.push(chartDataShown.grouping2);
      }
      const { fromDateStr, toDateStr } = getFromToDate(
        chartDataShown.period_option
      );
      const getStartDate = () => {
        if (chartDataShown.period_option === "custom") {
          return chartDataShown.period_start;
        } else {
          return fromDateStr
            ? fromDateStr.toISOString()
            : moment().endOf("day").subtract(20, "days").toISOString();
        }
      };
      const getEndDate = () => {
        if (chartDataShown.period_option === "custom") {
          return chartDataShown.period_end;
        } else {
          return toDateStr
            ? toDateStr.toISOString()
            : moment().endOf("day").subtract(7, "days").toISOString();
        }
      };
      const requestParams = {
        organization_id: indivFacilityMode
          ? null
          : organizationId
          ? parseInt(organizationId)
          : null,
        start_date: getStartDate(),
        end_date: getEndDate(),
        municipality_id: chartDataShown.municipality_filter?.split(","),
        category_id: chartDataShown.category_filter?.split(","),
        label_id: chartDataShown.label_filter?.split(","),
        aggregation: chartDataShown.aggregation,
        group: groupArray,
        metrics: chartDataShown.metrics,
        type: chartDataShown.type,
        time_unit: chartDataShown.time_unit,
        compare_to_prev: chartDataShown.compare_to_prev,
        location_id: chartDataShown.location_id,
      };

      if (_.isEqual(requestParams, lastFetchParams.current)) {
        return;
      }
      lastFetchParams.current = requestParams;

      let chartDetailsDataTemp = chartDetailsData;
      const chartDetailsForLinesDataTemp: OrgTimeSeriesMetricsData[] = [];
      const prevYearChartDetailsForLinesDataTemp: OrgTimeSeriesMetricsData[] =
        [];

      const fetchChartsData = async () => {
        // Don't fetch data if the user's organization ID is not yet set, unless in individual facility mode
        if (!indivFacilityMode && !organizationId) {
          return;
        }

        switch (chartDataShown.type) {
          case "line":
            const requestParameter = {
              ...requestParams,
              ...(chartDataShown.location_id
                ? { location_id: [chartDataShown.location_id] }
                : {}),
            };

            let groupedResponse: OrgGroupedTimeSeriesMetricsData;
            let singleResponse: OrgTimeSeriesMetricsData;

            // Line graphs can have single calls of single data (eg: simple line of overall impressions)
            // Single calls of multiple data (line graph of top 5 municipalities which only calls the API once),
            // Or multiple calls of single data (eg. impressions by impression type - where individual lines are called once to the API)
            // lineMetricsArray = API calls to be made. The Group data handles what happens when data comes back as a group.
            // Promise.all is returned as a result first to preserve the order of promises and therefore consistency in order and color of lines.
            const result = await Promise.all(
              // need update
              lineMetricsArray.map(async (item: any) => {
                return await DataSvc.getChartDataByTimeSeries({
                  ...requestParameter,
                  metric: item,
                });
              })
            );
            result.forEach((response: any, index) => {
              // Grouped data
              if (response.data[0] && Array.isArray(response.data[0].data)) {
                groupedResponse = response;
                const { data, organization_id, end_date, start_date } =
                  groupedResponse;
                data.forEach((item) => {
                  chartDetailsForLinesDataTemp.push({
                    title: [item.label],
                    organization_id,
                    start_date,
                    end_date,
                    data: item.data,
                  });
                });
              } else {
                singleResponse = response;
                chartDetailsForLinesDataTemp.push({
                  ...response,
                  title: lineMetricsArray[index],
                });
              }
            });

            if (usePrevYear) {
              const prevYearResults = await Promise.all(
                lineMetricsArray.map(async (item: any) => {
                  const { start_date, end_date } = requestParams;
                  return await DataSvc.getChartDataByTimeSeries({
                    ...requestParameter,
                    start_date: dayjs(start_date)
                      .subtract(1, "year")
                      .toISOString(),
                    end_date: dayjs(end_date).subtract(1, "year").toISOString(),
                    metric: item,
                  });
                })
              );

              prevYearResults.forEach((response: any, index) => {
                // Grouped data
                if (response.data[0] && Array.isArray(response.data[0].data)) {
                  const prevYearGroupedResponse =
                    response as OrgGroupedTimeSeriesMetricsData;
                  const { data, organization_id } = prevYearGroupedResponse;
                  data.forEach((item) => {
                    prevYearChartDetailsForLinesDataTemp.push({
                      title: [item.label],
                      organization_id,
                      start_date: prevYearGroupedResponse.start_date,
                      end_date: prevYearGroupedResponse.end_date,
                      data: item.data,
                    });
                  });
                } else {
                  prevYearChartDetailsForLinesDataTemp.push({
                    ...response,
                    title: lineMetricsArray[index],
                    start_date: singleResponse.start_date,
                    end_date: singleResponse.end_date,
                  });
                }
              });
            }
            break;
          case "bar":
            chartDetailsDataTemp =
              await DataSvc.getChartDataByGroupAggregations({
                ...requestParams,
                metric: JSON.parse(chartDataShown.metrics),
              });
            break;
          case "pie":
            chartDetailsDataTemp =
              await DataSvc.getChartDataByGroupAggregations({
                ...requestParams,
                metric: JSON.parse(chartDataShown.metrics),
              });
            break;
          case "table":
            chartDetailsDataTemp =
              await DataSvc.getChartDataByGroupAggregations({
                ...requestParams,
                metric: JSON.parse(chartDataShown.metrics),
                sort: "groups",
              });
            break;
          default:
            break;
        }
      };

      fetchChartsData().then(() => {
        setChartDetailsData(_.cloneDeep(chartDetailsDataTemp));
        setChartDetailsForLinesData(_.cloneDeep(chartDetailsForLinesDataTemp));
        setPrevYearChartDetailsForLinesData(
          _.cloneDeep(prevYearChartDetailsForLinesDataTemp)
        );
      });
    }

    // eslint-disable-next-line
  }, [chartDataShown, organizationId, usePrevYear]);

  // on Apply Filter Condition
  const onApplyFilterCondition = (
    conditionFormDataApply: FacilityFilterConditionData
  ) => {
    setConditionFormData(conditionFormDataApply);

    const municipality_filterValue: number[] = [];
    conditionFormDataApply.municipalities.forEach((item) => {
      item.muniList.forEach((itemMenu) => {
        municipality_filterValue.push(itemMenu.id);
      });
    });

    const category_filterValue = conditionFormDataApply.categories.map(
      (item) => {
        return item.id;
      }
    );
    const label_filterValue = conditionFormDataApply.labels.map((item) => {
      return item.id;
    });

    setChartDataShown({
      ...chartDataShown,
      municipality_filter:
        municipality_filterValue.length > 0
          ? municipality_filterValue.join(",")
          : null,
      category_filter:
        category_filterValue.length > 0 ? category_filterValue.join(",") : null,
      label_filter:
        label_filterValue.length > 0 ? label_filterValue.join(",") : null,
    });

    setShownModalFilterCondition(false);

    onUpdate?.(chartDataShown.id, {
      municipality_filter: flatten(
        conditionFormDataApply.municipalities.map((m) =>
          m.muniList.map((l) => l.id)
        )
      ).join(),
      category_filter: conditionFormDataApply.categories
        .map((c) => c.id)
        .join(),
      label_filter: conditionFormDataApply.labels.map((l) => l.id).join(),
    });
  };

  // on Click Clear Filter Condition
  const onClickClearFilterCondition = () => {
    setConditionFormData({
      ...defaultConditionFormData,
    });

    setChartDataShown({
      ...chartDataShown,
      municipality_filter: null,
      category_filter: null,
      label_filter: null,
    });

    onUpdate?.(chartDataShown.id, {
      municipality_filter: "",
      category_filter: "",
      label_filter: "",
    });
  };

  // has Condition Filter
  const hasConditionFilter = () => {
    let has = true;

    if (
      conditionFormData.healthIndex.length === 0 &&
      conditionFormData.categories.length === 0 &&
      conditionFormData.labels.length === 0
    ) {
      has = false;
    }

    let hasMunicipalities = false;
    conditionFormData.municipalities.forEach((item) => {
      if (item.muniList.length > 0) {
        hasMunicipalities = true;
      }
    });

    return has || hasMunicipalities;
  };

  // get Labels List
  const getLabelsList = (dataArray: any[]) => {
    const newDataArray: string[] = dataArray.map((item) => {
      return item.name;
    });

    return newDataArray.join(", ");
  };

  // onClickDateApply
  const onClickDateApply = (formData: AnalysisChartFilterData) => {
    setChartDataShown({
      ...chartDataShown,
      period_option: formData.periodDropdownValue,
      period_start: new Date(formData.fromDate).toISOString(),
      period_end: new Date(formData.toDate).toISOString(),
    });

    onUpdate?.(chartDataShown.id, {
      period_option: formData.periodDropdownValue,
      period_start: new Date(formData.fromDate).toISOString(),
      period_end: new Date(formData.toDate).toISOString(),
    });
  };

  const onApplyUsePrevYear = (usePrevYear: boolean) => {
    setUsePrevYear(usePrevYear);

    setChartDataShown((prev) => ({ ...prev, compare_to_prev: usePrevYear }));

    onUpdate?.(chartDataShown.id, {
      compare_to_prev: usePrevYear,
    });
  };

  return (
    <div className="collection-chart-area">
      {!!chartDataShown && (
        <>
          <OrgChartControls
            indivFacilityMode={indivFacilityMode}
            chartIndex={chartIndex}
            totalLength={totalLength}
            onClickMoveUp={onClickMoveUp}
            onClickMoveDown={onClickMoveDown}
            chartDataShown={chartDataShown}
            chartDetailsData={chartDetailsData}
            chartDetailsForLinesData={chartDetailsForLinesData}
            prevYearChartDetailsForLinesData={prevYearChartDetailsForLinesData}
            onClickEditChart={onClickEditChart}
            timeUnit={timeUnit}
          />

          <div className="date-bar flex-grid">
            {chartDataShown.template_name && (
              <div className="temlplate-txt">
                {t("template")}: {chartDataShown.template_name}
              </div>
            )}
            <DateRangeArea
              chartData={chartData}
              onClickApply={(formData: AnalysisChartFilterData) => {
                onClickDateApply(formData);
              }}
            />
          </div>
          {!indivFacilityMode && (
            <ModalFilterConditionSection
              hasConditionFilter={hasConditionFilter()}
              setShownModalFilterCondition={setShownModalFilterCondition}
              conditionFormData={conditionFormData}
              onApplyFilterCondition={onApplyFilterCondition}
              onClickClearFilterCondition={onClickClearFilterCondition}
              getLabelsList={getLabelsList}
              shownModalFilterCondition={shownModalFilterCondition}
              organizationId={organizationId}
              me={me}
            />
          )}

          <div className="chart-area">
            {chartDataShown.type === "bar" && (
              <BarChart
                chartDetailsData={chartDetailsData}
                chartId={chartDataShown.id}
              />
            )}

            {chartDataShown.type === "line" && (
              <LineChart
                chartDetailsData={chartDetailsForLinesData}
                prevYearChartDetailsData={prevYearChartDetailsForLinesData}
                usePrevYear={usePrevYear}
                timeUnit={timeUnit}
                skipLabelTranslation={
                  !!(chartDataShown.grouping1 || chartDataShown.grouping2)
                }
                setTimeUnit={(unit: "day" | "week" | "month") => {
                  onUpdate?.(chartDataShown.id, {
                    time_unit: unit,
                  });
                  setTimeUnit(unit);
                }}
                setUsePrevYear={onApplyUsePrevYear}
                chartId={chartDataShown.id}
                aggregation={chartDataShown.aggregation}
              />
            )}

            {chartDataShown.type === "pie" && (
              <CircleChart
                chartDetailsData={chartDetailsData}
                chartId={chartDataShown.id}
              />
            )}

            {chartDataShown.type === "table" && (
              <TableChart
                chartDetailsData={chartDetailsData}
                chartId={chartDataShown.id}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default withRouter(CollectionChartArea);
